// ===========================================
// Registration Fees List (#374)
// ===========================================
// Each paid registration fee of the family with its child and what became of it.
// Rendered only when the server sent fees (it sends [] without registration_fees.read).
// Never says "Unpaid": a missing fee is not provable from the data.

import { Box, Button, Chip, IconButton, Link, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link as RouterLink } from 'react-router-dom';
import type { RegistrationFeeView } from '../../../api/familyPayments.api';
import { PAYMENT_METHOD_LABELS, formatPaymentDay } from '../../../utils/invoiceDisplay';
import { formatCurrencyFromDollars } from '../../../utils/currency';

interface RegistrationFeesListProps {
  fees: RegistrationFeeView[];
  schoolSlug: string | undefined;
  timeZone: string;
  canAssign: boolean;
  onAssign: (fee: RegistrationFeeView) => void;
  /** Edit / delete of a MANUAL fee (operations_invoices write / delete), moved here from the status line. */
  canEdit?: boolean;
  canDelete?: boolean;
  onEdit?: (fee: RegistrationFeeView) => void;
  onDelete?: (fee: RegistrationFeeView) => void;
  /** #374: Not proceeding on a HELD fee (registration_fees.write). */
  canForfeit?: boolean;
  onForfeit?: (fee: RegistrationFeeView) => void;
  /** #374: undo it (the owner tier only). */
  canReverseForfeit?: boolean;
  onReverseForfeit?: (fee: RegistrationFeeView) => void;
  /** #374 P7: clear a Stripe refund flag (the owner tier only). */
  canClearRefundFlag?: boolean;
  onClearRefundFlag?: (fee: RegistrationFeeView) => void;
  /** Close a Stripe-refunded fee as Refunded (the owner tier only). */
  onCloseAsRefunded?: (fee: RegistrationFeeView) => void;
}

const iconButtonSx = { p: { xs: 1.5, sm: 0.5 }, minWidth: { xs: 44 }, minHeight: { xs: 44 } };

/** P7: what Stripe refunded, and (quietly) a flag the owner cleared. */
function RefundFlagNote({ fee, timeZone }: { fee: RegistrationFeeView; timeZone: string }) {
  const flag = fee.refundFlag;
  if (!flag) return null;
  if (fee.state === 'REFUNDED') {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Stripe refunded {formatCurrencyFromDollars(flag.amount)} on {formatPaymentDay(flag.at, timeZone)}.
      </Typography>
    );
  }
  if (flag.active) {
    const credited = fee.state === 'APPLIED';
    return (
      <Typography variant="caption" color={credited ? 'warning.main' : 'text.secondary'} sx={{ display: 'block' }}>
        Stripe refunded {formatCurrencyFromDollars(flag.amount)} on {formatPaymentDay(flag.at, timeZone)}.
        {credited ? ' Credited and refunded: take the credit off if the family should not have both.' : ' Not credited.'}
      </Typography>
    );
  }
  if (flag.cleared) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Refund flag cleared: {flag.cleared.note}
        {' (' + [flag.cleared.byName, formatPaymentDay(flag.cleared.at, timeZone)].filter(Boolean).join(', ') + ')'}
      </Typography>
    );
  }
  return null;
}

function StateLine({ fee, schoolSlug, timeZone }: { fee: RegistrationFeeView; schoolSlug: string | undefined; timeZone: string }) {
  switch (fee.state) {
    case 'REFUNDED':
      return <Chip size="small" variant="outlined" label="Refunded" />;
    case 'REFUND_FLAGGED':
      return (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label="Refund flagged"
          sx={{ height: 'auto', maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
        />
      );
    case 'HELD':
      return (
        <Chip
          size="small"
          color={fee.childActive === false ? 'default' : 'info'}
          variant="outlined"
          label={
            fee.childActive === false
              ? `Held (${fee.student?.name ?? 'the child'} is not active: not credited)`
              : `Held: credited on ${fee.student?.name ?? "the child"}'s first invoice`
          }
          // Wrap on a phone instead of truncating (360px).
          sx={{ height: 'auto', maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
        />
      );
    case 'APPLIED': {
      const short =
        fee.credit && parseFloat(fee.credit.creditedAmount) < parseFloat(fee.credit.worth)
          ? ` (credited ${formatCurrencyFromDollars(fee.credit.creditedAmount)} of ${formatCurrencyFromDollars(fee.credit.worth)})`
          : '';
      return (
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <Chip size="small" color="success" variant="outlined" label="Applied" />
          {fee.credit && (
            <Typography variant="body2">
              <Link component={RouterLink} to={`/${schoolSlug}/admin/invoices/${fee.credit.invoiceId}`}>
                {fee.credit.invoiceNumber}
              </Link>
              {short}
            </Typography>
          )}
        </Stack>
      );
    }
    case 'FORFEITED':
      return (
        <Stack spacing={0.25}>
          <Chip
            size="small"
            variant="outlined"
            label={`Not proceeding${fee.forfeit?.reason ? `: ${fee.forfeit.reason}` : ''}`}
            sx={{ alignSelf: 'flex-start', height: 'auto', maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
          />
          {fee.forfeit && (
            <Typography variant="caption" color="text.secondary">
              {fee.forfeit.detail ? `${fee.forfeit.detail} · ` : ''}
              {fee.forfeit.byName ? `${fee.forfeit.byName}, ` : ''}
              {formatPaymentDay(fee.forfeit.at, timeZone)}. The school keeps the fee.
            </Typography>
          )}
        </Stack>
      );
    case 'UNASSIGNED':
      return <Chip size="small" color="warning" variant="outlined" label="Paid, no child assigned" />;
    case 'NOT_CREDITED':
    default:
      return <Chip size="small" variant="outlined" label="Paid" />;
  }
}

export default function RegistrationFeesList({
  fees,
  schoolSlug,
  timeZone,
  canAssign,
  onAssign,
  canEdit = false,
  canDelete = false,
  onEdit,
  onDelete,
  canForfeit = false,
  onForfeit,
  canReverseForfeit = false,
  onReverseForfeit,
  canClearRefundFlag = false,
  onClearRefundFlag,
  onCloseAsRefunded,
}: RegistrationFeesListProps) {
  return (
    <Stack spacing={1} aria-label="Registration fees">
      {fees.map((fee) => (
        <Box
          key={fee.id}
          data-testid={`registration-fee-${fee.id}`}
          sx={{
            display: 'flex',
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2">
              <strong>Registration fee{fee.student ? `: ${fee.student.name}` : ''}</strong>
              {' · '}
              {formatCurrencyFromDollars(fee.amount)} · {PAYMENT_METHOD_LABELS[fee.method]} ·{' '}
              {formatPaymentDay(fee.paidAt, timeZone)}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <StateLine fee={fee} schoolSlug={schoolSlug} timeZone={timeZone} />
              <RefundFlagNote fee={fee} timeZone={timeZone} />
            </Box>
            {fee.studentMatch === 'ONLY_CHILD' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Check the child: matched as the family's only child (the names differ)
                {canAssign && fee.state !== 'FORFEITED' && (
                  <Button size="small" onClick={() => onAssign(fee)} sx={{ ml: 1, minHeight: { xs: 44 } }}>
                    Change
                  </Button>
                )}
              </Typography>
            )}
          </Box>
          {/* A forfeited fee stays as the school kept it (/qa P4 L2): Undo Not proceeding first. */}
          {fee.isManual && fee.state !== 'FORFEITED' && fee.state !== 'REFUNDED' && ((canEdit && onEdit) || (canDelete && onDelete)) && (
            <Stack direction="row" spacing={0.5}>
              {canEdit && onEdit && (
                <IconButton size="small" aria-label="Edit registration payment" sx={iconButtonSx} onClick={() => onEdit(fee)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {canDelete && onDelete && (
                <IconButton size="small" aria-label="Delete registration payment" sx={iconButtonSx} onClick={() => onDelete(fee)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          )}
          {(fee.state === 'UNASSIGNED' || (fee.state === 'REFUND_FLAGGED' && fee.student === null)) && canAssign && (
            <Button size="small" onClick={() => onAssign(fee)} sx={{ whiteSpace: 'nowrap', minHeight: { xs: 44 } }}>
              Assign to child
            </Button>
          )}
          {(fee.state === 'REFUND_FLAGGED' || fee.state === 'APPLIED') && fee.refundFlag?.active && canClearRefundFlag && onClearRefundFlag && (
            <Button size="small" onClick={() => onClearRefundFlag(fee)} sx={{ whiteSpace: 'nowrap', minHeight: { xs: 44 } }}>
              Clear refund flag
            </Button>
          )}
          {fee.state === 'REFUND_FLAGGED' && fee.refundFlag?.active && canClearRefundFlag && onCloseAsRefunded && (
            <Button size="small" onClick={() => onCloseAsRefunded(fee)} sx={{ whiteSpace: 'nowrap', minHeight: { xs: 44 } }}>
              Close as refunded
            </Button>
          )}
          {(fee.state === 'HELD' || (fee.state === 'REFUND_FLAGGED' && fee.student !== null)) && canForfeit && onForfeit && (
            <Button size="small" onClick={() => onForfeit(fee)} sx={{ whiteSpace: 'nowrap', minHeight: { xs: 44 } }}>
              Not proceeding
            </Button>
          )}
          {fee.state === 'FORFEITED' && canReverseForfeit && onReverseForfeit && (
            <Button size="small" onClick={() => onReverseForfeit(fee)} sx={{ whiteSpace: 'nowrap', minHeight: { xs: 44 } }}>
              Undo Not proceeding
            </Button>
          )}
        </Box>
      ))}
    </Stack>
  );
}
